import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';

import App from 'application/app';
import { addApolloState } from 'application/graphql/apollo-client';
import { CenteredLayout } from 'application/layouts/centered-layout';
import GeneralLayout from 'application/layouts/general-layout';
import GET_CONTENT_PAGE, {
  ContentPageModel,
  GetContentPageQuery,
  GetContentPageQueryVars,
} from 'features/content-page/graphql/get-content-page';
import ContentPage from 'features/content-page/graphql/page/content-page';

interface CustomPageProps {
  sport?: { id: string; name: string; alias: string };
  page?: ContentPageModel;
}

const IndexPage = dynamic(() => import('features/main/pages/index-page'));

const CustomPage: NextPage<CustomPageProps> = ({ sport, page }) => {
  const { t } = useTranslation(['seo']);

  if (sport) {
    return (
      <GeneralLayout
        title={sport.name}
        description={t(`seo:${sport.name}:description`)}
        imgPreview="/android-chrome-512x512.png"
      >
        <IndexPage sport={sport.id} />
      </GeneralLayout>
    );
  }

  return (
    <GeneralLayout
      title={page!.title}
      description={page!.description}
      imgPreview="/android-chrome-512x512.png"
    >
      <CenteredLayout>
        <ContentPage page={page!} />
      </CenteredLayout>
    </GeneralLayout>
  );
};

export const getServerSideProps: GetServerSideProps<CustomPageProps> = async ({
  req: { headers },
  res,
  query,
}) => {
  const app = await App.init({ headers, res });

  const url = query.url as string;

  // Страница вида спорта
  const sport = app.config?.sports.find((item) => item.alias === url);
  if (sport) {
    return addApolloState(app.apolloClient, {
      props: {
        sport,
        appConfig: app.config,
        ...(await app.getServerSideTranslations()),
      },
    });
  }
  // Контентная страница
  const { data, error } = await app.apolloClient.query<
    GetContentPageQuery,
    GetContentPageQueryVars
  >({
    query: GET_CONTENT_PAGE,
    variables: { url: (query.url as string) || '' },
  });

  if (error || data.getContentPage.page === null) {
    return {
      notFound: true,
    };
  }

  return addApolloState(app.apolloClient, {
    props: {
      page: data.getContentPage.page,
      appConfig: app.config,
      ...(await app.getServerSideTranslations()),
    },
  });
};

export default CustomPage;
